/**
 * WebView Authentication Hook
 * Handles WebView authentication flow
 */

import { useEffect, useState, useCallback } from 'react';
import { WebViewAuthService } from '../services/authService';
import { WebViewMessageService } from '../services/messageService';
import { useWebViewContext } from './useWebViewContext';
import { WEBVIEW_CONSTANTS } from '../utils/constants';

interface UseWebViewAuthReturn {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  authError: Error | null;
  authenticate: () => Promise<void>;
  logout: () => Promise<void>;
}

export function useWebViewAuth(): UseWebViewAuthReturn {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<Error | null>(null);
  
  const { isWebView, externalProps } = useWebViewContext();
  
  const authService = WebViewAuthService.getInstance();
  const messageService = WebViewMessageService.getInstance();
  
  // TEMPORARY: Skip WebView auth entirely to prevent infinite loop
  const skipWebViewAuth = true;

  const authenticate = useCallback(async () => {
    if (!externalProps?.authConfig) {
      setAuthError(new Error('No auth config available'));
      return;
    }

    setIsAuthenticating(true);
    setAuthError(null);

    try {
      await authService.authenticate(externalProps.authConfig);
      setIsAuthenticated(true);
      
      // Send success message to parent
      messageService.sendAuthSuccess(externalProps.authConfig.userInfo.id);
      
      // Store auth state
      localStorage.setItem(
        WEBVIEW_CONSTANTS.STORAGE_KEYS.AUTH_STATE,
        JSON.stringify({
          authenticated: true,
          timestamp: Date.now(),
          userId: externalProps.authConfig.userInfo.id,
        })
      );

      // Navigation will be handled by RouteGuard/InitialRoute
    } catch (error) {
      console.error('WebView authentication failed:', error);
      setAuthError(error as Error);
      setIsAuthenticated(false);
      
      // Send error message to parent
      messageService.sendAuthError(error as Error);
    } finally {
      setIsAuthenticating(false);
    }
  }, [externalProps, authService, messageService]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      
      // Clear stored auth state
      localStorage.removeItem(WEBVIEW_CONSTANTS.STORAGE_KEYS.AUTH_STATE);
      localStorage.removeItem(WEBVIEW_CONSTANTS.STORAGE_KEYS.EXTERNAL_PROPS);
      
      // Navigation will be handled by RouteGuard/InitialRoute
      // Reload to trigger re-authentication flow
      window.location.href = '/';
    } catch (error) {
      console.error('WebView logout failed:', error);
      setAuthError(error as Error);
    }
  }, [authService]);

  // Auto-authenticate when in WebView with valid props
  useEffect(() => {
    if (skipWebViewAuth) {
      console.log('Skipping WebView authentication (temporary)');
      return;
    }
    
    if (isWebView && externalProps?.authConfig && !isAuthenticated && !isAuthenticating) {
      authenticate();
    }
  }, [isWebView, externalProps, isAuthenticated, isAuthenticating, authenticate]);

  // Check stored auth state on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem(WEBVIEW_CONSTANTS.STORAGE_KEYS.AUTH_STATE);
    if (storedAuth) {
      try {
        const authState = JSON.parse(storedAuth);
        if (authState.authenticated && authService.isAuthenticated()) {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Failed to parse stored auth state');
      }
    }
  }, [authService]);

  return {
    isAuthenticated,
    isAuthenticating,
    authError,
    authenticate,
    logout,
  };
}